import { useCallback, useEffect, useState } from 'react';
import { getWorkflowExclusions, postWorkflowExclusion, deleteWorkflowExclusion } from '../services';
import { IWorkflowExclusionItem } from '../types';
import { resolveErrorMessage } from './hookUtils';

export interface UseWorkflowExclusionsResult {
	data: IWorkflowExclusionItem[] | null;
	loading: boolean;
	error: string | null;
	submitting: boolean;
	refetch: () => void;
	addExclusion: (workflowId: string, reason: string) => Promise<void>;
	removeExclusion: (workflowId: string) => Promise<void>;
}

export function useWorkflowExclusions(): UseWorkflowExclusionsResult {
	const [data, setData] = useState<IWorkflowExclusionItem[] | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [fetchCount, setFetchCount] = useState(0);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(null);

		getWorkflowExclusions()
			.then((items) => {
				if (!cancelled) {
					setData(items);
					setLoading(false);
				}
			})
			.catch((err) => {
				if (!cancelled) {
					setError(resolveErrorMessage(err));
					setLoading(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [fetchCount]);

	const refetch = useCallback(() => setFetchCount((n) => n + 1), []);

	const addExclusion = useCallback(
		async (workflowId: string, reason: string) => {
			setSubmitting(true);
			setError(null);
			try {
				await postWorkflowExclusion(workflowId, reason);
				refetch();
			} catch (err) {
				setError(resolveErrorMessage(err));
			} finally {
				setSubmitting(false);
			}
		},
		[refetch],
	);

	const removeExclusion = useCallback(
		async (workflowId: string) => {
			if (!data) return;
			const previousData = data;

			// Actualización optimista
			setData((prev) => (prev ? prev.filter((item) => item.workflowId !== workflowId) : prev));

			try {
				await deleteWorkflowExclusion(workflowId);
			} catch (err) {
				setData(previousData);
				setError(resolveErrorMessage(err));
			}
		},
		[data],
	);

	return { data, loading, error, submitting, refetch, addExclusion, removeExclusion };
}
